import { useState } from 'react';
import axios from 'axios';
import { Send, CheckCircle, Calendar } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import SEO from '@/components/SEO';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CALENDLY_URL = "https://calendly.com/tridenterprise-info/30min";
const WHATSAPP_ICON = "https://customer-assets.emergentagent.com/job_d9a33add-2dfd-4326-bbed-949961573d64/artifacts/hx48xypq_whatsapp.png";
const LOCATION_ICON = "https://customer-assets.emergentagent.com/job_d9a33add-2dfd-4326-bbed-949961573d64/artifacts/qcdhf6rg_location.png";
const MAIL_ICON = "https://customer-assets.emergentagent.com/job_d9a33add-2dfd-4326-bbed-949961573d64/artifacts/wmk5drle_mail.png";
const CLOCK_ICON = "https://customer-assets.emergentagent.com/job_d9a33add-2dfd-4326-bbed-949961573d64/artifacts/nzfbrr8s_business%20hours.png";

// Custom icon components using images
const LocationIcon = ({ className }) => (
  <img src={LOCATION_ICON} alt="Location" className={className} />
);

const WhatsAppIcon = ({ className }) => (
  <img src={WHATSAPP_ICON} alt="WhatsApp" className={className} />
);

const MailIcon = ({ className }) => (
  <img src={MAIL_ICON} alt="Email" className={className} />
);

const ClockIcon = ({ className }) => (
  <img src={CLOCK_ICON} alt="Business Hours" className={className} />
);

const contactInfo = [
  {
    icon: LocationIcon,
    title: 'Office Location',
    details: ['31, SVR Sadan, Road No 6', 'Jawahar Nagar, Hyderabad', 'Telangana, INDIA 500020']
  },
  {
    icon: WhatsAppIcon,
    title: 'Call / Whatsapp',
    details: ['+91 91 609 19 609'],
    link: 'https://wa.me/919160919609'
  },
  {
    icon: MailIcon,
    title: 'Email',
    details: ['info@tridenterprise.com']
  },
  {
    icon: ClockIcon,
    title: 'Business Hours',
    details: ['24/7 Support Available']
  }
];

const industries = [
  'Financial Services',
  'Healthcare',
  'Retail',
  'Manufacturing',
  'Technology',
  'Government',
  'Other'
];

const projectTypes = [
  'Oracle ERP Implementation',
  'Cloud Migration',
  'Custom Application Development',
  'Managed Services',
  'IT Staffing',
  'General Inquiry'
];

export default function ContactPage() {
  const [consultationForm, setConsultationForm] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    message: ''
  });
  
  const [proposalForm, setProposalForm] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    industry: '',
    projectType: '',
    timeline: '',
    budget: '',
    message: ''
  });
  
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleConsultationSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, {
        ...consultationForm,
        lead_type: 'consultation'
      });
      setSubmitted(true);
      toast.success('Consultation request submitted successfully!');
      setConsultationForm({ name: '', email: '', company: '', phone: '', message: '' });
    } catch (error) {
      toast.error('Failed to submit request. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleProposalSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, {
        name: proposalForm.name,
        email: proposalForm.email,
        company: proposalForm.company,
        phone: proposalForm.phone,
        message: `Industry: ${proposalForm.industry}\nProject Type: ${proposalForm.projectType}\nTimeline: ${proposalForm.timeline}\nBudget: ${proposalForm.budget}\n\n${proposalForm.message}`,
        lead_type: 'proposal'
      });
      setSubmitted(true);
      toast.success('Proposal request submitted successfully!');
      setProposalForm({
        name: '', email: '', company: '', phone: '', 
        industry: '', projectType: '', timeline: '', budget: '', message: ''
      });
    } catch (error) {
      toast.error('Failed to submit request. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="pt-20" data-testid="contact-page">
      <SEO 
        title="Contact Us"
        description="Contact Trid Enterprise Solutions for Oracle ERP implementation, cloud migration, and custom solutions. Schedule a consultation or request a proposal today."
        keywords="Contact Oracle Consultant, Schedule Consultation, Request Proposal, Oracle ERP Contact, Enterprise Solutions Contact"
      />
      {/* Hero Section */}
      <section className="section-dark py-20 md:py-32">
        <div className="container-custom">
          <div className="max-w-3xl">
            <span className="eyebrow mb-4 inline-block">Contact Us</span>
            <h1 className="heading-xl text-white mb-6 font-heading">
              Let's Start Your
              <span className="block text-oracle-red">Transformation</span>
            </h1>
            <p className="text-slate-300 font-body text-lg md:text-xl leading-relaxed">
              Ready to transform your enterprise with Oracle solutions? Schedule a consultation or request a proposal. Our experts are here to help.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="section-light py-8 md:py-12 border-b border-slate-200">
        <div className="container-custom">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
            {contactInfo.map((info, index) => (
              <div 
                key={info.title}
                className="flex flex-col sm:flex-row items-start space-y-2 sm:space-y-0 sm:space-x-3 md:space-x-4"
                data-testid={`contact-info-${index}`}
              >
                <div className="w-10 h-10 md:w-12 md:h-12 flex items-center justify-center flex-shrink-0">
                  <info.icon className="w-5 h-5 md:w-6 md:h-6" />
                </div>
                <div>
                  <h3 className="font-heading text-sm md:text-base font-bold text-slate-900 mb-0.5 md:mb-1">
                    {info.title}
                  </h3>
                  {info.details.map((detail, i) => (
                    info.link ? (
                      <a 
                        key={i} 
                        href={info.link} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="font-body text-xs md:text-sm text-slate-600 hover:text-oracle-red transition-colors block"
                      >
                        {detail}
                      </a>
                    ) : (
                      <p key={i} className="font-body text-xs md:text-sm text-slate-600">
                        {detail}
                      </p>
                    )
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Forms Section */}
      <section className="section-light py-12 md:py-20 lg:py-32" data-testid="contact-forms">
        <div className="container-custom">
          <div className="grid lg:grid-cols-5 gap-8 lg:gap-12">
            {/* Forms */}
            <div className="lg:col-span-3 order-2 lg:order-1">
              <Tabs defaultValue="consultation" className="w-full">
                <TabsList className="grid w-full grid-cols-2 mb-6 md:mb-8 h-auto">
                  <TabsTrigger 
                    value="consultation"
                    className="data-[state=active]:bg-oracle-red data-[state=active]:text-white font-body text-xs md:text-sm py-2.5 md:py-3"
                    data-testid="tab-consultation"
                  >
                    <Calendar className="w-3 h-3 md:w-4 md:h-4 mr-1 md:mr-2" />
                    Schedule Consultation
                  </TabsTrigger>
                  <TabsTrigger 
                    value="proposal"
                    className="data-[state=active]:bg-oracle-red data-[state=active]:text-white font-body text-xs md:text-sm py-2.5 md:py-3"
                    data-testid="tab-proposal"
                  >
                    <Send className="w-3 h-3 md:w-4 md:h-4 mr-1 md:mr-2" />
                    Request Proposal
                  </TabsTrigger>
                </TabsList>

                {/* Consultation Form */}
                <TabsContent value="consultation">
                  <Card className="card-enterprise">
                    <CardHeader className="p-4 md:p-6">
                      <CardTitle className="font-heading text-xl md:text-2xl text-slate-900">
                        Schedule a Consultation
                      </CardTitle>
                      <p className="font-body text-slate-600 text-xs md:text-sm">
                        Fill out the form below and we'll reach out to schedule your consultation.
                      </p>
                    </CardHeader>
                    <CardContent className="p-4 md:p-6 pt-0">
                      {submitted ? (
                        <div className="text-center py-6 md:py-8">
                          <CheckCircle className="w-12 h-12 md:w-16 md:h-16 text-green-500 mx-auto mb-4" />
                          <h3 className="font-heading text-lg md:text-xl font-bold text-slate-900 mb-2">
                            Request Submitted!
                          </h3>
                          <p className="font-body text-slate-600 text-sm">
                            We'll be in touch within 24 hours.
                          </p>
                          <Button 
                            className="mt-4"
                            onClick={() => setSubmitted(false)}
                          >
                            Submit Another Request
                          </Button>
                        </div>
                      ) : (
                        <form onSubmit={handleConsultationSubmit} className="space-y-4 md:space-y-6">
                          <div className="grid md:grid-cols-2 gap-3 md:gap-4">
                            <div className="space-y-1.5 md:space-y-2">
                              <Label htmlFor="cons-name" className="font-body text-sm">Full Name *</Label>
                              <Input
                                id="cons-name"
                                value={consultationForm.name}
                                onChange={(e) => setConsultationForm({...consultationForm, name: e.target.value})}
                                required
                                className="font-body"
                                data-testid="consultation-name"
                              />
                            </div>
                            <div className="space-y-1.5 md:space-y-2">
                              <Label htmlFor="cons-email" className="font-body text-sm">Email *</Label>
                              <Input
                                id="cons-email"
                                type="email"
                                value={consultationForm.email}
                                onChange={(e) => setConsultationForm({...consultationForm, email: e.target.value})}
                                required
                                className="font-body"
                                data-testid="consultation-email"
                              />
                            </div>
                          </div>
                          <div className="grid md:grid-cols-2 gap-3 md:gap-4">
                            <div className="space-y-1.5 md:space-y-2">
                              <Label htmlFor="cons-company" className="font-body text-sm">Company *</Label>
                              <Input
                                id="cons-company"
                                value={consultationForm.company}
                                onChange={(e) => setConsultationForm({...consultationForm, company: e.target.value})}
                                required
                                className="font-body"
                                data-testid="consultation-company"
                              />
                            </div>
                            <div className="space-y-1.5 md:space-y-2">
                              <Label htmlFor="cons-phone" className="font-body text-sm">Phone</Label>
                              <Input
                                id="cons-phone"
                                type="tel"
                                value={consultationForm.phone}
                                onChange={(e) => setConsultationForm({...consultationForm, phone: e.target.value})}
                                className="font-body"
                                data-testid="consultation-phone"
                              />
                            </div>
                          </div>
                          <div className="space-y-1.5 md:space-y-2">
                            <Label htmlFor="cons-message" className="font-body text-sm">Tell us about your needs</Label>
                            <Textarea
                              id="cons-message"
                              value={consultationForm.message}
                              onChange={(e) => setConsultationForm({...consultationForm, message: e.target.value})}
                              rows={4}
                              className="font-body"
                              placeholder="Describe your project requirements, timeline, and any specific challenges..."
                              data-testid="consultation-message"
                            />
                          </div>
                          <Button 
                            type="submit" 
                            className="w-full bg-oracle-red hover:bg-oracle-red-dark text-white font-body py-5"
                            disabled={loading}
                            data-testid="consultation-submit"
                          >
                            {loading ? 'Submitting...' : 'Request Consultation'}
                          </Button>
                        </form>
                      )}
                    </CardContent>
                  </Card>
                </TabsContent>

                {/* Proposal Form */}
                <TabsContent value="proposal">
                  <Card className="card-enterprise">
                    <CardHeader className="p-4 md:p-6">
                      <CardTitle className="font-heading text-xl md:text-2xl text-slate-900">
                        Request a Proposal
                      </CardTitle>
                      <p className="font-body text-slate-600 text-xs md:text-sm">
                        Provide details about your project for a customized proposal.
                      </p>
                    </CardHeader>
                    <CardContent className="p-4 md:p-6 pt-0">
                      {submitted ? (
                        <div className="text-center py-6 md:py-8">
                          <CheckCircle className="w-12 h-12 md:w-16 md:h-16 text-green-500 mx-auto mb-4" />
                          <h3 className="font-heading text-lg md:text-xl font-bold text-slate-900 mb-2">
                            Proposal Request Submitted!
                          </h3>
                          <p className="font-body text-slate-600 text-sm">
                            Our team will prepare a customized proposal for you.
                          </p>
                          <Button 
                            className="mt-4"
                            onClick={() => setSubmitted(false)}
                          >
                            Submit Another Request
                          </Button>
                        </div>
                      ) : (
                        <form onSubmit={handleProposalSubmit} className="space-y-4 md:space-y-6">
                          <div className="grid md:grid-cols-2 gap-3 md:gap-4">
                            <div className="space-y-1.5 md:space-y-2">
                              <Label htmlFor="prop-name" className="font-body text-sm">Full Name *</Label>
                              <Input
                                id="prop-name"
                                value={proposalForm.name}
                                onChange={(e) => setProposalForm({...proposalForm, name: e.target.value})}
                                required
                                className="font-body"
                                data-testid="proposal-name"
                              />
                            </div>
                            <div className="space-y-1.5 md:space-y-2">
                              <Label htmlFor="prop-email" className="font-body text-sm">Email *</Label>
                              <Input
                                id="prop-email"
                                type="email"
                                value={proposalForm.email}
                                onChange={(e) => setProposalForm({...proposalForm, email: e.target.value})}
                                required
                                className="font-body"
                                data-testid="proposal-email"
                              />
                            </div>
                          </div>
                          <div className="grid md:grid-cols-2 gap-3 md:gap-4">
                            <div className="space-y-1.5 md:space-y-2">
                              <Label htmlFor="prop-company" className="font-body text-sm">Company *</Label>
                              <Input
                                id="prop-company"
                                value={proposalForm.company}
                                onChange={(e) => setProposalForm({...proposalForm, company: e.target.value})}
                                required
                                className="font-body"
                                data-testid="proposal-company"
                              />
                            </div>
                            <div className="space-y-1.5 md:space-y-2">
                              <Label htmlFor="prop-phone" className="font-body text-sm">Phone</Label>
                              <Input
                                id="prop-phone"
                                type="tel"
                                value={proposalForm.phone}
                                onChange={(e) => setProposalForm({...proposalForm, phone: e.target.value})}
                                className="font-body"
                                data-testid="proposal-phone"
                              />
                            </div>
                          </div>
                          <div className="grid md:grid-cols-2 gap-3 md:gap-4">
                            <div className="space-y-1.5 md:space-y-2">
                              <Label className="font-body text-sm">Industry *</Label>
                              <Select 
                                value={proposalForm.industry}
                                onValueChange={(value) => setProposalForm({...proposalForm, industry: value})}
                              >
                                <SelectTrigger className="font-body" data-testid="proposal-industry">
                                  <SelectValue placeholder="Select industry" />
                                </SelectTrigger>
                                <SelectContent>
                                  {industries.map((industry) => (
                                    <SelectItem key={industry} value={industry}>
                                      {industry}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                            <div className="space-y-1.5 md:space-y-2">
                              <Label className="font-body text-sm">Project Type *</Label>
                              <Select 
                                value={proposalForm.projectType}
                                onValueChange={(value) => setProposalForm({...proposalForm, projectType: value})}
                              >
                                <SelectTrigger className="font-body" data-testid="proposal-project-type">
                                  <SelectValue placeholder="Select project type" />
                                </SelectTrigger>
                                <SelectContent>
                                  {projectTypes.map((type) => (
                                    <SelectItem key={type} value={type}>
                                      {type}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                          </div>
                          <div className="grid md:grid-cols-2 gap-3 md:gap-4">
                            <div className="space-y-1.5 md:space-y-2">
                              <Label className="font-body text-sm">Expected Timeline</Label>
                              <Select 
                                value={proposalForm.timeline}
                                onValueChange={(value) => setProposalForm({...proposalForm, timeline: value})}
                              >
                                <SelectTrigger className="font-body" data-testid="proposal-timeline">
                                  <SelectValue placeholder="Select timeline" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="immediate">Immediate (&lt; 1 month)</SelectItem>
                                  <SelectItem value="1-3months">1-3 Months</SelectItem>
                                  <SelectItem value="3-6months">3-6 Months</SelectItem>
                                  <SelectItem value="6-12months">6-12 Months</SelectItem>
                                  <SelectItem value="planning">Just Planning</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            <div className="space-y-1.5 md:space-y-2">
                              <Label className="font-body text-sm">Budget Range</Label>
                              <Select 
                                value={proposalForm.budget}
                                onValueChange={(value) => setProposalForm({...proposalForm, budget: value})}
                              >
                                <SelectTrigger className="font-body" data-testid="proposal-budget">
                                  <SelectValue placeholder="Select budget" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="50-100k">$50K - $100K</SelectItem>
                                  <SelectItem value="100-250k">$100K - $250K</SelectItem>
                                  <SelectItem value="250-500k">$250K - $500K</SelectItem>
                                  <SelectItem value="500k-1m">$500K - $1M</SelectItem>
                                  <SelectItem value="1m+">$1M+</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                          </div>
                          <div className="space-y-1.5 md:space-y-2">
                            <Label htmlFor="prop-message" className="font-body text-sm">Project Details *</Label>
                            <Textarea
                              id="prop-message"
                              value={proposalForm.message}
                              onChange={(e) => setProposalForm({...proposalForm, message: e.target.value})}
                              rows={4}
                              required
                              className="font-body"
                              placeholder="Describe your project scope, objectives, current systems, and any specific requirements..."
                              data-testid="proposal-message"
                            />
                          </div>
                          <Button 
                            type="submit" 
                            className="w-full bg-oracle-red hover:bg-oracle-red-dark text-white font-body py-5"
                            disabled={loading}
                            data-testid="proposal-submit"
                          >
                            {loading ? 'Submitting...' : 'Request Proposal'}
                          </Button>
                        </form>
                      )}
                    </CardContent>
                  </Card>
                </TabsContent>
              </Tabs>
            </div>

            {/* Calendly Sidebar */}
            <div className="lg:col-span-2 order-1 lg:order-2">
              <Card className="card-dark lg:sticky lg:top-28">
                <CardHeader className="p-4 md:p-6">
                  <CardTitle className="font-heading text-lg md:text-xl text-white flex items-center">
                    <Calendar className="w-4 h-4 md:w-5 md:h-5 mr-2 text-oracle-red" />
                    Book a Meeting
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-4 md:p-6 pt-0">
                  <p className="font-body text-slate-400 text-xs md:text-sm mb-4 md:mb-6">
                    Prefer to schedule directly? Use our calendar to book a time that works for you.
                  </p>
                  
                  {/* Calendly Inline Widget */}
                  <div 
                    className="bg-white rounded-sm overflow-hidden"
                    data-testid="calendly-embed"
                  >
                    <iframe
                      src={`${CALENDLY_URL}?hide_gdpr_banner=1&background_color=ffffff&text_color=0f172a&primary_color=C74634`}
                      width="100%"
                      height="400"
                      frameBorder="0"
                      title="Schedule a consultation with Trid Enterprise"
                      className="min-h-[350px] md:min-h-[400px]"
                    />
                  </div>

                  <div className="mt-6 md:mt-8 pt-4 md:pt-6 border-t border-slate-700">
                    <h4 className="font-heading text-sm md:text-base font-bold text-white mb-3 md:mb-4">
                      What to Expect
                    </h4>
                    <ul className="space-y-2 md:space-y-3">
                      {[
                        '30-minute discovery call',
                        'Discuss your requirements',
                        'Receive preliminary recommendations',
                        'No obligation consultation'
                      ].map((item) => (
                        <li key={item} className="flex items-center space-x-2">
                          <CheckCircle className="w-3 h-3 md:w-4 md:h-4 text-oracle-red flex-shrink-0" />
                          <span className="font-body text-xs md:text-sm text-slate-300">{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
